import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { paths } from "@clevis/shared-types";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { getRtcConfig, CandidateQueue, writeSdp, writeCandidate } from "../lib/remoteSupportRtc";
import { attachControlReplay } from "../lib/remoteControlReplay";
import { useAuth } from "../hooks/useAuth";

// Owns the remote-support session + WebRTC state at the SHELL level so the
// share (and the admin's control) survives SPA navigation. The Support page
// card is just UI over this context; a floating pill on every page shows the
// live share and always offers End. (Ported from skuru.)

export interface RemoteSupportState {
  sessionId: string | null;
  code: string | null;
  expiresAtMs: number | null;
  status: string | null;
  sharing: boolean;
  busy: boolean;
  error: string | null;
  canShare: boolean;
  shareSurface: string | null;
  start: () => Promise<void>;
  share: () => Promise<void>;
  end: () => Promise<void>;
}

function detectCanShare(): boolean {
  if (typeof navigator === "undefined") return false;
  const md = navigator.mediaDevices as (MediaDevices & { getDisplayMedia?: unknown }) | undefined;
  return typeof md?.getDisplayMedia === "function" && window.isSecureContext;
}

const Ctx = createContext<RemoteSupportState | null>(null);

export function useRemoteSupport(): RemoteSupportState | null {
  return useContext(Ctx);
}

export function RemoteSupportProvider({ children }: { children: ReactNode }) {
  const { tenantId } = useAuth();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [expiresAtMs, setExpiresAtMs] = useState<number | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [shareSurface, setShareSurface] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canShare = detectCanShare();

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const unsubSignalsRef = useRef<(() => void) | null>(null);
  const detachControlRef = useRef<(() => void) | null>(null);

  function teardownRtc() {
    detachControlRef.current?.();
    detachControlRef.current = null;
    unsubSignalsRef.current?.();
    unsubSignalsRef.current = null;
    pcRef.current?.close();
    pcRef.current = null;
    for (const t of streamRef.current?.getTracks() ?? []) t.stop();
    streamRef.current = null;
    setSharing(false);
    setShareSurface(null);
  }

  function resetAll() {
    teardownRtc();
    setSessionId(null);
    setCode(null);
    setExpiresAtMs(null);
    setStatus(null);
  }

  useEffect(() => {
    if (!sessionId) return;
    return onSnapshot(doc(db, paths.remoteSupportSession(sessionId)), (snap) => {
      const s = (snap.get("status") as string | undefined) ?? null;
      if (s === "ended" || s === "expired") {
        resetAll();
      } else {
        setStatus(s);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => teardownRtc, []);

  async function start() {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await call<{ sessionId: string; code: string; expiresAtMs: number }>(
        "createRemoteSupportSession",
        { tenantId },
        { mutation: false },
      );
      setSessionId(res.sessionId);
      setCode(res.code);
      setExpiresAtMs(res.expiresAtMs);
      setStatus("pending");
    } catch (err) {
      setError((err as Error).message ?? "Couldn't start a remote support session.");
    } finally {
      setBusy(false);
    }
  }

  async function share() {
    if (!sessionId) return;
    setError(null);
    try {
      const mediaDevices = navigator.mediaDevices as MediaDevices & {
        getDisplayMedia(o?: unknown): Promise<MediaStream>;
      };
      const stream = await mediaDevices.getDisplayMedia({
        video: { frameRate: 8 },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: "include",
      });
      streamRef.current = stream;

      const vTrack = stream.getVideoTracks()[0];
      const settings = (vTrack?.getSettings?.() ?? {}) as { displaySurface?: string };
      setShareSurface(settings.displaySurface ?? null);

      const pc = new RTCPeerConnection(await getRtcConfig());
      pcRef.current = pc;
      for (const track of stream.getTracks()) pc.addTrack(track, stream);

      // Control is only honored for a tab share — on a window/monitor share
      // the admin's coordinates don't map to this page, so stay view-only.
      const controllable = settings.displaySurface === "browser" || settings.displaySurface === undefined;
      const dc = pc.createDataChannel("control");
      dc.onopen = () => {
        detachControlRef.current?.();
        if (controllable) {
          detachControlRef.current = attachControlReplay(dc);
        } else {
          try {
            dc.send(JSON.stringify({ t: "noControl" }));
          } catch {
            /* best effort */
          }
        }
      };

      if (vTrack) {
        vTrack.onended = () => void end();
      }

      const queue = new CandidateQueue(pc);
      pc.onicecandidate = (e) => {
        if (e.candidate) void writeCandidate(sessionId, "customer", e.candidate);
      };

      unsubSignalsRef.current = onSnapshot(collection(db, paths.remoteSupportSignals(sessionId)), (snap) => {
        for (const change of snap.docChanges()) {
          if (change.type !== "added" && change.type !== "modified") continue;
          const d = change.doc;
          const data = d.data();
          if (d.id === "answer" && !pc.currentRemoteDescription) {
            void pc
              .setRemoteDescription({ type: "answer", sdp: (data.sdp as string) ?? "" })
              .then(() => queue.markReady())
              .catch(() => setError("Couldn't establish the connection."));
          } else if (data.role === "admin" && data.candidate) {
            void queue.add(data.candidate as RTCIceCandidateInit);
          }
        }
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await writeSdp(sessionId, "offer", offer);
      setSharing(true);
    } catch (err) {
      if ((err as Error).name !== "NotAllowedError") {
        setError((err as Error).message ?? "Couldn't start sharing your screen.");
      }
      teardownRtc();
    }
  }

  async function end() {
    const active = sessionId;
    resetAll();
    if (!active) return;
    setBusy(true);
    try {
      await call("endRemoteSupportSession", { sessionId: active }, { mutation: false });
    } catch {
      /* session doc listener reflects reality */
    } finally {
      setBusy(false);
    }
  }

  const value: RemoteSupportState = {
    sessionId,
    code,
    expiresAtMs,
    status,
    sharing,
    busy,
    error,
    canShare,
    shareSurface,
    start,
    share,
    end,
  };

  return (
    <Ctx.Provider value={value}>
      {children}
      {sharing ? (
        <div className="fixed bottom-4 right-4 z-[9999] flex items-center gap-3 rounded-full bg-ink py-2 pl-4 pr-2 text-white shadow-lg">
          <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-danger" />
          <span className="text-xs font-medium">Sharing your screen with Clevis support</span>
          <button
            type="button"
            onClick={() => void end()}
            className="rounded-full bg-danger px-3 py-1.5 text-xs font-semibold transition-colors hover:opacity-90"
          >
            End
          </button>
        </div>
      ) : null}
    </Ctx.Provider>
  );
}
